import React, { useEffect, useState } from 'react'
import cartHandler from '@components/services/cart'
import { IDeviceInfo, useUI } from '@components/ui/context'
import {
  ADDED_TO_WISH,
  PROD_ADDED_SUCCESSFULLY,
  INSUFFICIENT_STOCK,
  PROD_ADDED,
} from '@components/utils/textVariables'
import 'swiper/css'

import 'swiper/css/navigation'
import {
  AppsFlyerKeys,
  AppsFlyerParams,
  EmptyGuid,
  FacebookEvents,
  NEXT_CREATE_WISHLIST,
} from '@components/utils/constants'
import axios from '@services/axiosInterceptorInstance'
import { matchStrings } from '@framework/utils/parse-util'
import {
  getBrowserIdForFBConversion,
  getCategories,
  getColor,
  getCommonAppsFlyerValue,
  getCurrentPage,
  getIpAddressForFBConversion,
  getDateOfBirthHash,
  getGenderHash,
  isCartAssociated,
  openLoginView,
  openNewCartPage,
  removePrecedingSlash,
  getColorAndSize,
  eventIdForFB,
} from '@framework/utils/app-util'
import { useRouter } from 'next/router'
import useSection from 'hooks/useSection'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { facebookEventHandler } from 'hooks/facebook-conversion'
import { getSHA256Hash } from '@framework/utils/cipher'
import { useGoogleAnalyticsEvent } from 'hooks/useGAEvent'
import dynamic from 'next/dynamic'
import { IProduct } from '@components/utils/model/product.model'

const RelatedProductsWithGroupSection = dynamic(
  () => import('./helper/RelatedProductsWithGroupSection.component'),
  {
    ssr: false,
  }
)

declare const window: any
declare const AF: any

interface IProps {
  recordId: string
  deviceInfo: IDeviceInfo
  pageType: string
  relatedProducts: any
  setQuickViewProductSlug: (payload: string | null) => void
}

export default function RelatedProductsWithGroup({
  deviceInfo,
  pageType,
  relatedProducts: relatedProductData,
  recordId,
  setQuickViewProductSlug,
}: IProps) {
  const relatedProducts = relatedProductData.relatedProducts

  const { openCart, addToWishlist, setAlert, setSidebarView, cartItems } =
    useUI()

  const trackMoEngageEvent = useMoEngageEvent()
  const { isOnlyMobile } = deviceInfo
  const { basketId, setCartItems, user } = useUI()
  const { handleSection } = useSection()
  const { asPath } = useRouter()
  const { isMobile, isIPadorTablet } = deviceInfo
  const router = useRouter()
  const trackGoogleAnalyticsEvent = useGoogleAnalyticsEvent()
  const isChannelStore = router?.query?.channelStore ? true : false

  let currentPage = getCurrentPage()
  const onProductQuickView = (product: IProduct | null, pid?: any) => {
    setQuickViewProductSlug(product?.slug ?? null)
    if (currentPage) {
      if (product?.name) {
        let deviceCheck = ''
        if (isMobile || isIPadorTablet) {
          deviceCheck = 'Mobile'
        } else {
          deviceCheck = 'Desktop'
        }
        recordGA4Event(window, GA_EVENT.QUICK_VIEW_CLICK, {
          ecommerce: {
            device: deviceCheck,
            items: {
              product_name: product?.name,
              position: pid + 1,
              product_price: product?.price?.raw?.withTax,
              color: product?.variantGroupCode,
              category: product?.classification?.mainCategoryName,
              current_page: currentPage,
              header: 'You May Also Like',
            },
            loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
          },
        })

        recordGA4Event(window, GA_EVENT.POPUP_VIEW, {
          product_name: product?.name,
          category: product?.classification?.mainCategoryName,
          page: window?.location?.href,
          position: pid + 1,
          // color: product?.variantGroupCode,
          color: product?.attributes.find(
            (x: any) => x?.key === 'product.colours'
          )?.value,
          price: product?.price?.raw?.withTax,
          current_page: currentPage,
          item_brand: product?.brand,
          product_price: product?.price?.raw?.withTax,
          quantity: 1,
          item_id: product?.sku,
          item_variant: product?.attributes.find(
            (x: any) => x?.key === 'product.colours'
          )?.value,
          category2: product?.classification?.category,
          item_var_id: product?.stockCode,
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        })
      }
    }

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.QUICK_VIEW,
      {
        URL: window?.location?.href,
        Product_name: product?.name,
        Product_category: product?.classification?.mainCategoryName,
        Product_color: product?.variantGroupCode,
        Price: product?.price?.raw?.withTax,
      },
      window
    )
  }

  // const getWordBreakLimit = getWordBreakLimitFn(isOnlyMobile, deviceInfo)

  const getlineItemSizeWithoutSlug = (product: any) => {
    if (isChannelStore && product?.size !== '' && product?.size !== undefined) {
      return product?.size
    } else if (isChannelStore && product?.customAttributes) {
      return product?.customAttributes
        ?.filter((vp: any) => vp?.key === 'clothing.size')[0]
        ?.fieldText?.toUpperCase()
        ?.replaceAll('-', '')
        ?.replaceAll('SIZE', '')
    } else {
      return product?.variantProducts
        ?.filter((vp: any) => vp?.stockCode === product?.stockCode)[0]
        ?.variantAttributes?.filter(
          (va: any) => va?.fieldCode === 'clothing.size'
        )[0]
        ?.fieldValue?.toUpperCase()
        ?.replaceAll('-', '')
        ?.replaceAll('SIZE', '')
    }
  }

  const addToCartGAEvent = async (productData: any) => {
    const eventId = eventIdForFB()
    trackGoogleAnalyticsEvent(
      GA_EVENT.ADD_TO_CART,
      {
        ecommerce: {
          items: [
            {
              item_name: productData?.name,
              item_brand: productData?.brand,
              item_category2: getCategories(productData).category2,
              item_category: getCategories(productData).category,
              item_variant: getColorAndSize(productData),
              color: getColor(productData),
              quantity: productData?.qty,
              item_id: productData?.sku,
              item_size: getlineItemSizeWithoutSlug(productData),
              price: productData?.price?.raw?.withTax,
              item_var_id: productData?.stockCode,
              item_list_name: getCategories(productData).category,
              index: 1,
            },
          ],
          cart_quantity: 1,
          total_value: productData?.price?.raw?.withTax,
          current_page: getCurrentPage(),
          event_id: eventId,
          section_title: 'Product Detail',
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      },
      window
    )

    const mobileNumber = user?.mobile || user?.telephone
    let facebookeventData = {
      data: [
        {
          event_name: FacebookEvents.ADD_TO_CART,
          event_time: Math.floor(new Date().getTime() / 1000),
          event_id: eventId,
          action_source: 'website',
          user_data: {
            em: [user?.email ? await getSHA256Hash(user?.email) : ''],
            ph: mobileNumber ? await getSHA256Hash(mobileNumber) : null,
            fn: user?.firstName ? await getSHA256Hash(user?.firstName) : null,
            ln: user?.lastName ? await getSHA256Hash(user?.lastName) : null,
            client_user_agent: window.navigator.userAgent,
            fbp: getBrowserIdForFBConversion(),
            client_ip_address: (await getIpAddressForFBConversion()) || null,
            external_id: mobileNumber
              ? await getSHA256Hash(mobileNumber)
              : null,
            db: await getDateOfBirthHash(user),
            ge: await getGenderHash(user?.gender),
            ct: null,
            zp: null,
            country: await getSHA256Hash('in'),
          },
          custom_data: {
            currency: 'INR',
            num_items: 1,
            content_name: getCurrentPage(),
            value: productData?.price?.raw?.withTax,
            content_category: getCategories(productData).category,
            content_ids: [productData?.stockCode],
            content_type: 'product',
            contents: [
              {
                id: productData?.recordId ?? productData?.productId,
                quantity: 1,
                item_price: productData?.price?.raw?.withTax,
                delivery_category: 'home_delivery',
              },
            ],
          },
        },
      ],
    }

    facebookEventHandler(facebookeventData)
  }

  const addToCart = (product: any) => {
    const asyncAddToCart = async () => {
      const item = await cartHandler().addToCart(
        {
          basketId: basketId,
          productId: product.recordId,
          qty: 1,
          // manualUnitPrice: product.price.raw.withTax,
          stockCode: product.stockCode,
          userId: user.userId,
          isAssociated: isCartAssociated(cartItems), //user.isAssociated,
        },
        'ADD',
        { product },
        (item: any) => {
          // setCartItems(item)
          // openCart()
        }
      )
      // setCartItems(item)
      if (
        item?.message &&
        !matchStrings(item?.message, PROD_ADDED_SUCCESSFULLY, true)
      ) {
        setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
      } else if (item?.id === EmptyGuid) {
        setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
      } else {
        setAlert({ type: 'success', msg: PROD_ADDED })
        addToCartGAEvent(product)
        setCartItems(item)
        //         openCart();
        openNewCartPage(openCart, 'item_added')
      }
    }
    asyncAddToCart()
  }
  const insertToLocalWishlist = (product: any) => {
    addToWishlist(product)
    setAlert({ type: 'success', msg: ADDED_TO_WISH })
    // openWishlist()
  }
  const handleWishList = async (product: any) => {
    const accessToken = localStorage?.getItem('user')
    if (accessToken) {
      const createWishlist = async () => {
        try {
          const res = await axios.post(NEXT_CREATE_WISHLIST, {
            id: user?.userId,
            productId: product?.recordId,
            flag: true,
          })
          if (res?.data) {
            insertToLocalWishlist(product)
          }
        } catch (error) {
          console.error(':: error ', error)
        }
      }
      createWishlist()
    } else {
      openLoginView(asPath)
      setSidebarView('LOGIN_VIEW')
      // insertToLocalWishlist()
    }
    let productAvailability = 'Yes'
    if (product?.currentStock > 0) {
      productAvailability = 'Yes'
    } else {
      productAvailability = 'No'
    }

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.ADD_TO_WISHLIST,
      {
        URL: window?.location?.href,
        Product_name: product?.name,
        Product_category: product?.classification?.mainCategoryName,
        Product_category_2: product?.classification?.category,
        Product_color: '',
        Price: product?.price?.raw?.withTax,
      },
      window
    )

    const eventId = eventIdForFB()
    if (currentPage) {
      recordGA4Event(window, GA_EVENT.ADD_TO_WISHLIST, {
        ecommerce: {
          items: [
            {
              item_name: product?.name,
              item_brand: product?.brand,
              item_category2: getCategories(product).category2,
              item_category: getCategories(product).category,
              item_variant: product?.attributes.find(
                (x: any) => x?.key === 'product.colours'
              )?.value,
              color: product?.attributes.find(
                (x: any) => x?.key === 'product.colours'
              )?.value,
              quantity: 1,
              item_id: product?.sku,
              // item_size: getlineItemSizeWithoutSlug(product),
              price: product?.price?.raw?.withTax,
              item_var_id: product?.stockCode,
              item_list_name: getCategories(product).category,
              index: 1,
            },
          ],
          item_var_id: product?.stockCode,
          header: 'You May Also Like',
          current_page: currentPage,
          availability: productAvailability,
          event_id: eventId,
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      })
    }

    const mobileNumber = user?.mobile || user?.telephone
    let facebookeventData: any = {
      data: [
        {
          event_name: FacebookEvents.ADD_TO_WISHLIST,
          event_time: Math.floor(new Date().getTime() / 1000),
          event_id: eventId,
          action_source: 'website',
          user_data: {
            em: [user?.email ? await getSHA256Hash(user?.email) : ''],
            ph: mobileNumber ? await getSHA256Hash(mobileNumber) : null,
            fn: user?.firstName ? await getSHA256Hash(user?.firstName) : null,
            ln: user?.lastName ? await getSHA256Hash(user?.lastName) : null,
            client_user_agent: window.navigator.userAgent,
            fbp: getBrowserIdForFBConversion(),
            client_ip_address: (await getIpAddressForFBConversion()) || null,
            external_id: mobileNumber
              ? await getSHA256Hash(mobileNumber)
              : null,
            db: await getDateOfBirthHash(user),
            ge: await getGenderHash(user?.gender),
            ct: null,
            zp: null,
            country: await getSHA256Hash('in'),
          },
          custom_data: {
            currency: 'INR',
            content_name: getCurrentPage(),
            content_category: getCategories(product).category,
            content_ids: [product?.stockCode],
            content_type: 'product',
            contents: [
              {
                id: product?.recordId ?? product?.productId,
                quantity: 1,
                item_price: product?.price?.raw?.withTax,
                delivery_category: 'home_delivery',
              },
            ],
          },
        },
      ],
    }

    facebookEventHandler(facebookeventData)

    if (typeof AF !== 'undefined') {
      const commonEventValue = getCommonAppsFlyerValue()
      AF('pba', 'event', {
        eventType: 'EVENT',
        eventName: AppsFlyerKeys.AddToWishlist,
        eventRevenueCurrency: 'INR',
        eventValue: {
          [AppsFlyerParams.AFEventParam3]: currentPage,
          [AppsFlyerParams.AFEventParamContent]: product?.name,
          [AppsFlyerParams.AFEventParamContentId]: product?.stockCode,
          [AppsFlyerParams.AFEventParamPrice]:
            product?.price?.raw?.withTax + '',
          [AppsFlyerParams.AFEventParam5]:
            product?.classification?.mainCategoryName || '',
          [AppsFlyerParams.AFEventParam6]:
            product?.classification?.category || '',
          ...commonEventValue,
        },
      })
    }
  }

  function viewProductDetail(product: any, pid: number) {
    handleSection('You May Also Like')

    recordGA4Event(window, GA_EVENT.SELECT_ITEM, {
      ecommerce: {
        items: [
          {
            item_id: product?.sku,
            item_name: product?.name,
            price: product?.price?.raw?.withTax,
            item_brand: product?.brand,
            item_category: product?.classification?.mainCategoryName,
            item_category2: product?.classification?.category,
            item_variant: product?.attributes.find(
              (x: any) => x?.key === 'product.colours'
            )?.value,
            item_list_name: product?.classification?.category,
            item_list_id: product?.collections?.length
              ? product?.collections[product?.collections?.length - 1]
                  .collectionId
              : '',
            index: pid + 1,
          },
        ],
        color: product?.attributes.find(
          (x: any) => x?.key === 'product.colours'
        )?.value,
        position: pid + 1,
        item_var_id: product?.stockCode,
        current_page: currentPage,
        section_title: 'You May Also Like',
      },
    })

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.PRODUCT_VIEW,
      {
        URL: window?.location?.href,
        Product_name: product?.name,
        item_category: product?.classification?.mainCategoryName,
        item_category2: product?.classification?.category,
        Product_color: product?.variantGroupCode,
        Price: product?.price?.raw?.withTax,
        UTM: '',
      },
      window
    )

    recordGA4Event(window, GA_EVENT.VIEW_PROD_DETAILS, {
      category_selected: product?.name,
      header: window?.location.pathname,
      current_page: currentPage,
    })
  }

  // if (
  //   getRelatedProductAPIStatus === API_STATUS.NOT_STARTED ||
  //   getRelatedProductAPIStatus === API_STATUS.LOADING ||
  //   !relatedProducts.length
  // ) {
  //   return null
  // }

  const relatedProductGroup: any = {}

  relatedProducts?.forEach((item: any) => {
    item?.groupNameList?.forEach((_i: any, index: any) => {
      const relatedTypeCode = _i?.name

      if (!relatedProductGroup[relatedTypeCode]) {
        relatedProductGroup[relatedTypeCode] = []
      }

      relatedProductGroup[relatedTypeCode].push(item)
    })
  })

  return (
    <>
      <div className="flex flex-col">
        <div className="section-devider"></div>
      </div>
      <div className="px-0 mx-auto sm:container page-container sm:px-6">
        <RelatedProductsWithGroupSection
          isOnlyMobile={!!isOnlyMobile}
          relatedProductGroup={relatedProductGroup}
          pageType={pageType}
          viewProductDetail={(product: any, pid: number) => {
            viewProductDetail(product, pid)
          }}
          onProductQuickView={(product: any, pid: number) => {
            onProductQuickView(product, pid)
          }}
          handleWishList={(product: any) => {
            handleWishList(product)
          }}
          addToCart={(product: any) => {
            addToCart(product)
          }}
        />
      </div>
    </>
  )
}
